import { useState } from 'react';
import {
  REPORT_CONTACT_MAX,
  REPORT_DETAILS_MAX,
  REPORT_REASONS,
  type CreateReportRequest,
  type ReportReason,
} from '@markbird/shared';
import { useT } from '../i18n/useT';
import { Modal } from './Modal';

/**
 * Report a shared document to the operator.
 *
 * No account needed: the person who most needs this is usually the one a
 * document is about, not a markbird user. Reasons are radio buttons rather than
 * a select, so every option is visible at once and none reads as the default.
 * Contact is optional and says what it is for — a report that demands an email
 * address before it will send is a report some people will not make.
 */
export function ReportDialog({
  target,
  onClose,
}: {
  /** The share as its visitor reached it: `/s/:token` or `/p/:slug`. */
  target: { token: string } | { slug: string };
  onClose: () => void;
}) {
  const t = useT();
  const [reason, setReason] = useState<ReportReason | null>(null);
  const [details, setDetails] = useState('');
  const [contact, setContact] = useState('');
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason || state === 'sending') return;
    setState('sending');
    const body: CreateReportRequest = {
      ...target,
      reason,
      details: details.trim() || undefined,
      contact: contact.trim() || undefined,
    };
    try {
      const res = await fetch('/api/reports', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      });
      setState(res.ok ? 'sent' : 'error');
    } catch {
      setState('error');
    }
  };

  if (state === 'sent') {
    return (
      <Modal onClose={onClose} title={t('report.sentTitle')}>
        <p className="mb-modal-note" role="status">
          {t('report.sentBody')}
        </p>
        <div className="mb-modal-actions">
          <button type="button" className="mb-btn mb-btn-primary" onClick={onClose}>
            {t('common.close')}
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal onClose={onClose} title={t('report.title')}>
      <form onSubmit={(e) => void submit(e)} className="mb-report">
        <p className="mb-modal-note">{t('report.intro')}</p>

        <fieldset className="mb-report-reasons">
          <legend>{t('report.reasonLegend')}</legend>
          {REPORT_REASONS.map((r) => (
            <label key={r} className="mb-report-reason">
              <input
                type="radio"
                name="reason"
                value={r}
                checked={reason === r}
                onChange={() => setReason(r)}
              />
              <span>{t(`report.reason.${r}`)}</span>
            </label>
          ))}
        </fieldset>

        <label className="mb-report-field">
          <span>{t('report.details')}</span>
          <textarea
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            maxLength={REPORT_DETAILS_MAX}
            rows={4}
          />
        </label>

        <label className="mb-report-field">
          <span>{t('report.contact')}</span>
          <input
            type="email"
            value={contact}
            onChange={(e) => setContact(e.target.value)}
            maxLength={REPORT_CONTACT_MAX}
            autoComplete="email"
          />
          <small>{t('report.contactHint')}</small>
        </label>

        {state === 'error' && (
          <p className="mb-modal-note mb-import-error" role="alert">
            {t('report.failed')}
          </p>
        )}

        <div className="mb-modal-actions">
          <button type="button" className="mb-btn" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            className="mb-btn mb-btn-primary"
            disabled={!reason || state === 'sending'}
          >
            {state === 'sending' ? t('report.sending') : t('report.send')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
